// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { StyledTypography } from "@insite/mobius/Link";
import MenuDefaultProps from "@insite/mobius/Menu/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getProp from "@insite/mobius/utilities/getProp";
import { MegaMenuItem, MenuPresentationProps } from "@synd/MobiusOverrides/Menu";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xBaseTheme } from "@synd/XDL/types";
import { color, createLinkIconStyles, styledScrollbar } from "@synd/XDL/Utilities";
import { css } from "styled-components";

export default {
    defaultProps: {
        ...MenuDefaultProps,
        mergeCss: true,
        moreIconProps: {
            src: ChevronRight,
            size: 14,
            color: "common.ui7",
            css: css`
                position: absolute;
                right: 0;
                top: 3px;
                &,
                & svg {
                    width: auto;
                }
            `,
        },
        menuItemTypographyProps: {
            size: fontConfig.smallSingleLine.size,
            weight: fontConfig.weight.regular,
            css: css`
                line-height: ${fontConfig.smallSingleLine.lineHeight};
            `,
        },
        cssOverrides: {
            menu: css`
                max-height: 65vh;
                border: 1px solid ${color("common.ui2")};
                border-radius: 0 0 8px 8px;
                display: flex;
                flex-direction: column;
                background: ${color("common.white")};
                z-index: ${getProp("theme.zIndex.menu")};
                width: ${getProp("width", 324)}px;
                height: 100%;
                margin: 0;
                padding: 16px;
                position: relative;
                box-shadow: unset;

                &:has(.MenuItem[aria-expanded="true"]) {
                    border-bottom-right-radius: 0;

                    &.leftFlippedSubMenu {
                        border-radius: 0 0 8px 0;
                    }
                }
                ${MegaMenuItem} {
                    break-inside: avoid;
                }
                ${MegaMenuItem} + ${MegaMenuItem} {
                    margin-top: 24px;
                }
                > div {
                    ${styledScrollbar()};
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    row-gap: 14px;

                    > .MenuItem {
                        width: 100%;
                        &:hover,
                        &[data-open] {
                            svg,
                            ${TypographyStyle} {
                                color: ${color("common.ui10")};
                            }
                        }
                    }
                }

                ${({ currentDepth, width, isMegaMenu, numberOfColumns }: any) => {
                    if (!isMegaMenu && currentDepth > 1) {
                        return css`
                            border-bottom-left-radius: 0;

                            &.leftFlippedSubMenu {
                                border-radius: 0 0 0 8px;
                            }
                        `;
                    }

                    return css`
                        width: ${width ? `${width}px` : "324px"};

                        ${isMegaMenu &&
                        css`
                            border-bottom-right-radius: 0;
                            width: fit-content;
                            height: auto;
                            > div {
                                height: auto;
                                column-count: ${numberOfColumns};
                                column-gap: 24px;
                                display: block;
                                column-fill: balance;
                            }
                        `};
                    `;
                }}
            `,
            menuItem: css`
                a {
                    width: 100%;
                    min-height: fit-content;
                    justify-content: space-between;
                    ${TypographyStyle} {
                        font-weight: ${fontConfig.weight.medium};
                    }
                    &:hover {
                        background: unset;
                    }
                }
            `
        },
        megaMenuItemParentLink: {
            mergeCss: true,
            typographyProps: {
                variant: "h6",
                transform: "uppercase",
                css: css`
                    margin-bottom: 0;
                `,
            },
        },
        megaMenuItemLinksWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                row-gap: 14px;
                align-items: flex-start;
            `,
        },
        megaMenuItemLink: {
            typographyProps: {
                size: fontConfig.smallSingleLine.size,
                weight: fontConfig.weight.medium,
            },
            css: css`
                ${StyledTypography} {
                    color: ${color("text.main")};
                    line-height: ${fontConfig.smallSingleLine.lineHeight};
                }
                &&& {
                    padding: 0;
                }
            `,
        },

        megaMenuContentSection: {
            css: css`
                border: 1px solid ${color("common.ui2")};
                border-left: none;
                background-color: ${color("common.ui1")};
                border-radius: 0 0 8px 0;
                width: 324px;
                max-height: 65vh;
                height: auto;
                padding: 16px;
                ${styledScrollbar()};

                &:empty,
                &:has(> div.isc-content-block:empty) {
                    display: none;
                }
            `,
        },
        megaMenuContentLink: {
            ...createLinkIconStyles({
                icon: ChevronRight,
                sizeVariant: "xsmall",

                linkStyles: {
                    color: "primary",
                    icon: { position: "right" },
                    css: css`
                        margin-bottom: 12px;

                        ${IconWrapper} {
                            color: ${color("primary")};
                        }
                    `,
                },
            }),
        },
    } as MenuPresentationProps,
} as xBaseTheme["menu"];
